import { CheckCircle, Circle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { Separator } from '@/components/shadcn/separator'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { useGetApiCompaniesCurrentNotificationsIdDetails } from '@/api/generated/company-management-user/company-management-user'
import type { GenericInAppNotificationDefinitionResponse } from '@/api/model/genericInAppNotificationDefinitionResponse'

interface NotificationDetailDialogProps {
  notification: GenericInAppNotificationDefinitionResponse
  onClose: () => void
}

export function NotificationDetailDialog({ notification, onClose }: NotificationDetailDialogProps) {
  const { t } = useTranslation(['notifications', 'common'])
  const { data, isLoading } = useGetApiCompaniesCurrentNotificationsIdDetails(notification.id)
  const detail = (data as any)?.data ?? data

  const formatDate = (ts: string) =>
    new Date(ts.endsWith('Z') ? ts : ts + 'Z').toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{notification.title}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <p className="text-sm text-muted-foreground py-4">{t('detail.loading')}</p>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Read by */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {t('detail.readBy', { total: detail?.readBy?.length ?? 0 })}
              </p>
              <Separator className="mb-2" />
              <ScrollArea className="h-[160px]">
                <div className="flex flex-col gap-1.5 pr-2">
                  {detail?.readBy?.length > 0 ? detail.readBy.map((r: any, i: number) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="size-3.5 text-green-500 shrink-0" />
                        <span>{r.employee?.firstName} {r.employee?.lastName}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{formatDate(r.seenTimestamp)}</span>
                    </div>
                  )) : (
                    <p className="text-sm text-muted-foreground">{t('detail.noneRead')}</p>
                  )}
                </div>
              </ScrollArea>
            </div>

            <Separator />

            {/* Unread by */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {t('detail.unreadBy', { total: detail?.unreadBy?.length ?? 0 })}
              </p>
              <Separator className="mb-2" />
              <ScrollArea className="h-[160px]">
                <div className="flex flex-col gap-1.5 pr-2">
                  {detail?.unreadBy?.length > 0 ? detail.unreadBy.map((e: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Circle className="size-3.5 shrink-0" />
                      <span>{e.firstName} {e.lastName}</span>
                    </div>
                  )) : (
                    <p className="text-sm text-muted-foreground">{t('detail.allRead')}</p>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common:buttons.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
